import React from "react";
import Marquee from "react-fast-marquee";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  Calendar,
  MapPin,
  Clock,
  CheckCircle2,
  Sparkles,
  ShieldCheck,
  Instagram,
  Linkedin,
  Mail,
  Play,
} from "lucide-react";
import Orbs from "@/components/Orbs";
import RegistrationForm from "@/components/RegistrationForm";
import Reveal from "@/components/Reveal";
import ScrollProgress from "@/components/ScrollProgress";
import ProofCarousel from "@/components/ProofCarousel";
import { TEST_IDS } from "@/constants/testIds";


const VIDEO_URL =
  "https://assets.alwaysonsalesevent.com/always-on-sales-event/sales-video/Always-On-Sales-Video.mp4";

function FounderVideo() {
  const videoRef = React.useRef(null);
  const [unmuted, setUnmuted] = React.useState(false);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.loop = true;
    video.play().catch(() => {});
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!video.dataset.unmuted) {
          entry.intersectionRatio < 0.3 ? video.pause() : video.play().catch(() => {});
        }
      },
      { threshold: [0.3] }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const handleClick = () => {
    const video = videoRef.current;
    if (!video || unmuted) return;
    setUnmuted(true);
    video.dataset.unmuted = "1";
    video.loop = false;
    video.muted = false;
    video.currentTime = 0;
    video.play();
  };

  const handleEnded = () => {
    const video = videoRef.current;
    if (!video) return;
    // Reset back to idle: muted autoplay loop
    delete video.dataset.unmuted;
    video.muted = true;
    video.loop = true;
    video.currentTime = 0;
    video.play().catch(() => {});
    setUnmuted(false);
  };

  return (
    <div
      onClick={handleClick}
      style={{ position: "relative", cursor: unmuted ? "default" : "pointer", borderRadius: "8px", overflow: "hidden", background: "#000" }}
    >
      <video
        ref={videoRef}
        src={VIDEO_URL}
        playsInline
        preload="auto"
        onEnded={handleEnded}
        style={{ width: "100%", display: "block" }}
      />
      {!unmuted && (
        <div style={{
          position: "absolute", bottom: 14, right: 14,
          background: "rgba(0,0,0,0.55)", borderRadius: "50%",
          width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <line x1="23" y1="9" x2="17" y2="15" />
            <line x1="17" y1="9" x2="23" y2="15" />
          </svg>
        </div>
      )}
      {!unmuted && (
        <div style={{
          position: "absolute", bottom: 62, right: 14,
          background: "rgba(0,0,0,0.6)", color: "#fff",
          fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase",
          padding: "4px 8px", borderRadius: 4
        }}>
          Tap to watch
        </div>
      )}
    </div>
  );
}

const FOUNDER_IMG = "/founder-image-aos.jpeg";
const VENUE_IMG =
  "https://images.unsplash.com/photo-1596522354195-e84ae3c98731?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBhdWRpdG9yaXVtJTIwc3RhZ2V8ZW58MHx8fHwxNzgxNzIwODQ1fDA&ixlib=rb-4.1.0&q=85";

const SPONSORS = ["Coca-Cola", "Motown", "AT&T", "Pierre Cardin", "Def Jam", "Universal"];

const RECEIPTS = [
  "A six-figure acquisition offer for my agency — six months in.",
  "A private equity offer to join a company as an AI systems builder.",
  "A six-figure job offer from a startup to build AI infrastructure.",
  "Inbound consulting requests from law firms.",
  "Paid sponsorship opportunities from AI companies.",
];

const TAKEAWAYS = [
  {
    title: "AI that never logs off",
    body: "How AI agents answer customer questions instantly — even when you're closed.",
  },
  {
    title: "Where you're bleeding leads",
    body: "The exact spots in your funnel you're losing customers right now without knowing it.",
  },
  {
    title: "Real client case studies",
    body: "Live examples of small businesses already using Always On Sales to capture revenue.",
  },
  {
    title: "A non-technical path",
    body: "Exactly how to get this live in your own business in 7 days — without touching code.",
  },
];

const FAQS = [
  {
    q: "Do I need technical experience?",
    a: "No. Everything is built and trained once on your business — you don't touch code, manage servers, or maintain anything. You run your business; the AI agent runs itself.",
  },
  {
    q: "How much does AI cost?",
    a: "There's no monthly fee per client. You pay once: $1,997 for an existing website, or $3,997 if you need a website built too. The agent is yours — no recurring cost to keep it running.",
  },
  {
    q: "Can this work for my industry?",
    a: "Yes. It's built for any business where a single customer is worth real money — law firms, restaurants and venues, real estate, med spas, contractors, and similar service-based businesses. If a missed question could cost you a $500+ customer, this works for you.",
  },
  {
    q: "How long does implementation take?",
    a: "7 days from start to live, fully trained on your business and ready to capture leads.",
  },
  {
    q: "Can I try the AI?",
    a: "Yes — click the chat button in the bottom-right of this page right now and talk to a live version of the exact AI agent we install for clients.",
  },
];

const scrollToRegister = () => {
  const el = document.getElementById("register");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function LandingPage() {
  return (
    <main className="font-body">
      <ScrollProgress />
      {/* ============================= */}
      {/* SECTION 1 — HERO (DARK) */}
      {/* ============================= */}
      <section className="section-dark grain min-h-[100vh] flex flex-col">
        <Orbs variant="hero" />

        {/* Top bar */}
        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 pt-8 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="h-9 w-9 rounded-md flex items-center justify-center"
              style={{
                background:
                  "linear-gradient(135deg, #D4AF37 0%, #F3CE5E 100%)",
              }}
            >
              <Sparkles size={16} className="text-[#030712]" />
            </div>
            <div className="font-display font-bold tracking-tight text-white">
              FAVOR <span className="text-[#D4AF37]">STUDIO</span>
            </div>
          </div>
          <button
            onClick={scrollToRegister}
            className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white transition"
          >
            Register <ArrowRight size={14} />
          </button>
        </div>

        {/* Hero content */}
        <div className="relative z-10 flex-1 flex items-center">
          <div className="max-w-7xl mx-auto w-full px-6 py-16 md:py-24">
            <div className="max-w-4xl">
              <Reveal y={20} duration={700} delay={0}>
                <div className="eyebrow mb-6">
                  A Free, In-Person Event · Aug 1, 2026
                </div>
              </Reveal>
              <Reveal y={40} duration={900} delay={120} blur={6}>
                <h1
                  data-testid={TEST_IDS.heroHeadline}
                  className="font-display font-black text-white leading-[0.95] text-[clamp(3rem,9vw,7.5rem)] mb-6"
                >
                  Always On{" "}
                  <span
                    style={{
                      background:
                        "linear-gradient(135deg, #D4AF37 0%, #F3CE5E 60%, #D4AF37 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Sales.
                  </span>
                </h1>
              </Reveal>
              <Reveal y={24} duration={800} delay={320}>
                <p className="text-lg md:text-xl text-white/75 leading-relaxed max-w-2xl mb-10">
                  Build a website with an AI Agent that's always on selling —
                  capturing every lead, answering every question, guiding
                  customers toward booking. Even when you've logged off for the
                  day.
                </p>
              </Reveal>

              <Reveal y={20} duration={700} delay={480}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                  <button
                    data-testid={TEST_IDS.heroRegisterCta}
                    onClick={scrollToRegister}
                    className="btn-gold rounded-md"
                  >
                    Register For The Free Event
                    <ArrowRight size={18} />
                  </button>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/55">
                    Free · In-Person · Limited Seats
                  </div>
                </div>
              </Reveal>

              {/* Founder video */}
              <Reveal y={28} duration={900} delay={600} blur={4}>
                <div className="mt-10 max-w-3xl">
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.32em] text-white/45 mb-4">
                    <span className="h-px w-10 bg-white/15" />
                    Founder Note · 60–90 sec
                  </div>
                  <FounderVideo />
                </div>
              </Reveal>

              {/* Event meta strip */}
              <Reveal y={20} duration={700} delay={780}>
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
                  <MetaItem
                    icon={<MapPin size={16} />}
                    label="Venue"
                    value="Shaquille O'Neal Movie Theater"
                  />
                  <MetaItem
                    icon={<Calendar size={16} />}
                    label="Date"
                    value="August 1, 2026"
                  />
                  <MetaItem
                    icon={<Clock size={16} />}
                    label="Time"
                    value="TBA"
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div
          data-testid={TEST_IDS.marquee}
          className="relative z-10 py-8 border-t border-white/10"
        >
          <div className="text-[10px] tracking-[0.4em] uppercase text-white/40 mb-3 text-center">
            Our Team Has Worked With
          </div>
          <Marquee gradient={false} speed={45} pauseOnHover>
            {SPONSORS.map((s) => (
              <span key={s} className="ticker-text">
                {s}
                <span className="ticker-dot">●</span>
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* ============================= */}
      {/* SECTION 2 — CREDIBILITY (DARK) */}
      {/* ============================= */}
      <section
        data-testid={TEST_IDS.founderSection}
        className="section-dark grain py-24 md:py-32"
      >
        <Orbs variant="split-right" />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-center">
            <Reveal x={-40} y={20} duration={1000} blur={4}>
              <div className="relative">
                <div
                  className="overflow-hidden rounded-2xl"
                  style={{
                    border: "1px solid rgba(255,255,255,0.12)",
                    boxShadow:
                      "0 30px 80px rgba(0,85,255,0.18), 0 0 0 1px rgba(0,240,255,0.15)",
                  }}
                >
                  <img
                    src={FOUNDER_IMG}
                    alt="Lovefavor Alexander"
                    className="block w-full h-[460px] md:h-auto object-cover"
                  />
                </div>
                <div
                  className="absolute -bottom-5 -right-5 px-5 py-3 rounded-lg glass text-xs uppercase tracking-[0.2em] text-cyan-300"
                >
                  Founder · Favor Studio
                </div>
              </div>
            </Reveal>

            <div>
              <Reveal y={28} duration={800} delay={120}>
                <div className="eyebrow mb-5">Brought to you by Favor Studio</div>
              </Reveal>
              <Reveal y={32} duration={900} delay={200}>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black leading-[1.05] mb-6 text-white">
                  From zero to{" "}
                  <span className="text-[#00F0FF]">six-figure offers</span> in six months.
                </h2>
              </Reveal>
              <Reveal y={24} duration={800} delay={360}>
                <p className="text-white/75 text-lg leading-relaxed mb-8">
                  I taught myself AI. Then I got offered six figures to build it
                  for other companies. I'm Lovefavor Alexander, founder of Favor
                  Studio, an AI infrastructure agency based in New York. I build
                  AI systems for B2B firms and law firms, and I've deployed AI
                  employees that run automatically inside my own business every
                  single day.
                </p>
              </Reveal>

              <ul className="space-y-4 mb-8">
                {RECEIPTS.map((r, i) => (
                  <Reveal
                    key={r}
                    as="li"
                    x={-16}
                    y={12}
                    duration={650}
                    delay={500 + i * 90}
                    className="flex items-start gap-3"
                  >
                    <span className="receipt-check mt-0.5">
                      <CheckCircle2 size={14} />
                    </span>
                    <span className="text-white/85 leading-relaxed">{r}</span>
                  </Reveal>
                ))}
              </ul>

              <Reveal y={24} duration={800} delay={500 + RECEIPTS.length * 90 + 80}>
                <div
                  className="glass rounded-xl p-5 text-white/80 leading-relaxed italic space-y-3"
                >
                  <p>
                    These weren't lucky breaks. They happened because my AI
                    systems made my agency look like it had been operating for
                    six years — when it had only been operating for{" "}
                    <span className="text-[#D4AF37] not-italic font-semibold">
                      six months.
                    </span>
                  </p>
                  <p>
                    I invested over four figures in AI education before I ever
                    built a single system for other businesses. I studied the
                    frameworks, built the systems, got real results — and then
                    the offers started coming in.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============================= */}
      {/* PROOF CAROUSEL (DARK) */}
      {/* ============================= */}
      <ProofCarousel />

      {/* ============================= */}
      {/* SECTION 3 — PAIN (DARK) */}
      {/* ============================= */}
      <section className="section-dark grain py-28 md:py-36">
        <Orbs variant="split-left" />
        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <Reveal y={24} duration={700}>
            <div className="eyebrow mb-6">The hard truth</div>
          </Reveal>
          <Reveal y={40} duration={900} delay={120} blur={4}>
            <h2 className="font-display font-black text-white leading-[1.02] text-[clamp(2.5rem,6vw,5rem)] mb-10 max-w-4xl">
              Right now, you're losing customers{" "}
              <span className="text-[#D4AF37]">and don't know it.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-5xl">
            <Reveal y={24} duration={800} delay={280}>
              <p className="text-white/75 text-lg md:text-xl leading-relaxed">
                A customer visits your website with a question. Nobody answers.
                They leave.
              </p>
            </Reveal>
            <Reveal y={24} duration={800} delay={400}>
              <p className="text-white/75 text-lg md:text-xl leading-relaxed">
                You log off at 6pm. Your website goes silent. But customers don't
                stop browsing at 6pm.
              </p>
            </Reveal>
            <Reveal y={24} duration={900} delay={560} className="md:col-span-2 max-w-3xl">
              <p className="text-white/95 text-xl md:text-2xl font-display font-bold leading-snug">
                Every missed message is a missed booking. Every missed booking is
                missed revenue.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============================= */}
      {/* SECTION 4 — CONSEQUENCES (WHITE) */}
      {/* ============================= */}
      <section className="section-light py-28 md:py-36">
        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <div className="flex justify-end">
            <div className="max-w-3xl text-right">
              <Reveal x={32} y={20} duration={700}>
                <div className="eyebrow eyebrow-light mb-6">While you sleep</div>
              </Reveal>
              <Reveal x={32} y={32} duration={900} delay={120} blur={4}>
                <h2 className="font-display font-black text-[#030712] leading-[1.02] text-[clamp(2.25rem,5.5vw,4.5rem)] mb-10">
                  Your competitors are{" "}
                  <span className="text-[#0055ff]">already answering.</span>
                </h2>
              </Reveal>
              <Reveal y={24} duration={800} delay={280}>
                <p className="text-[#4B5563] text-lg md:text-xl leading-relaxed mb-6">
                  Enterprises and franchises already have AI agents working their
                  websites 24/7. They're capturing the customers you're losing.
                </p>
              </Reveal>
              <Reveal y={24} duration={800} delay={420}>
                <p className="text-[#4B5563] text-lg md:text-xl leading-relaxed mb-6">
                  The gap between businesses using AI and businesses that aren't
                  is widening every day — and it compounds.
                </p>
              </Reveal>
              <Reveal y={24} duration={900} delay={580}>
                <p className="text-[#030712] text-xl md:text-2xl font-display font-bold leading-snug">
                  This isn't a someday problem. It's costing you money right now.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============================= */}
      {/* SECTION 5 — SOLUTION (DARK) */}
      {/* ============================= */}
      <section className="section-dark grain py-32 md:py-40 border-t border-white/5">
        <Orbs variant="center" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="eyebrow mb-6 mx-auto">The fix</div>
          <h2 className="font-display font-black text-white leading-[0.98] text-[clamp(2.75rem,7vw,6.25rem)] mb-10">
            Always On Sales fixes this{" "}
            <span
              style={{
                background:
                  "linear-gradient(135deg, #D4AF37 0%, #F3CE5E 60%, #D4AF37 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              in one week.
            </span>
          </h2>
          <p className="text-white/80 text-lg md:text-xl leading-relaxed max-w-3xl mx-auto mb-10">
            We build and install an AI Agent directly into your website. It
            answers every question, captures every lead, and guides customers
            toward booking — 24/7. No monthly cost per client. No missed
            revenue. No extra hires.
          </p>
          <p className="font-display font-bold text-2xl md:text-3xl text-white mb-12">
            You run your business. Your website handles the rest.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            <Pillar label="24 / 7" sub="Online & answering" />
            <Pillar label="7 Days" sub="From start to live" />
            <Pillar label="$0 / mo" sub="No recurring fee" />
          </div>
        </div>
      </section>

      {/* ============================= */}
      {/* SECTION 6 — EVENT DETAILS (WHITE) */}
      {/* ============================= */}
      <section className="section-light py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal y={24} duration={800} className="max-w-3xl mb-14">
            <div className="eyebrow eyebrow-light mb-5">The event</div>
            <h2 className="font-display font-black text-[#030712] leading-[1.02] text-[clamp(2.5rem,5.5vw,4.75rem)]">
              What you'll walk away with.
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
            {TAKEAWAYS.map((t, i) => (
              <Reveal
                key={t.title}
                y={32}
                duration={700}
                delay={i * 120}
                scale={0.96}
                className="border border-[#030712]/12 rounded-xl p-6 hover:border-[#0055ff] hover:shadow-lg transition-all hover:-translate-y-1"
                style={{ background: "#F8FAFC" }}
              >
                <div
                  className="text-xs font-bold mb-4"
                  style={{ color: "#0055ff", letterSpacing: "0.2em" }}
                >
                  0{i + 1}
                </div>
                <h3 className="font-display font-bold text-xl mb-3 text-[#030712] leading-snug">
                  {t.title}
                </h3>
                <p className="text-[#4B5563] text-sm leading-relaxed">{t.body}</p>
              </Reveal>
            ))}
          </div>

          {/* Venue block */}
          <Reveal y={40} duration={1000} blur={3}>
            <div
              className="rounded-2xl overflow-hidden grid grid-cols-1 md:grid-cols-2"
              style={{
                border: "1px solid rgba(3,7,18,0.10)",
                background: "#F8FAFC",
              }}
            >
              <img
                src={VENUE_IMG}
                alt="Shaquille O'Neal Movie Theater"
                className="w-full h-72 md:h-full object-cover"
              />
              <div className="p-8 md:p-10 flex flex-col justify-center">
              <div className="eyebrow eyebrow-light mb-4">The venue</div>
              <h3 className="font-display font-black text-[#030712] text-3xl md:text-4xl leading-tight mb-6">
                Shaquille O'Neal Movie Theater.
              </h3>
              <div className="space-y-3 text-[#030712]/80 mb-8">
                <div className="flex items-center gap-3">
                  <Calendar size={18} className="text-[#0055ff]" />
                  <span className="font-medium">August 1, 2026</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock size={18} className="text-[#0055ff]" />
                  <span className="font-medium">Time TBA</span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin size={18} className="text-[#0055ff]" />
                  <span className="font-medium">In-Person · Limited Seats</span>
                </div>
                <div className="flex items-center gap-3">
                  <ShieldCheck size={18} className="text-[#0055ff]" />
                  <span className="font-medium">Free to attend</span>
                </div>
              </div>
              <button onClick={scrollToRegister} className="btn-ghost-light rounded-md w-fit">
                Reserve a Seat
              </button>
            </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============================= */}
      {/* SECTION 7 — FAQ (WHITE) */}
      {/* ============================= */}
      <section className="section-light py-24 md:py-32 border-t border-[#030712]/8">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal y={24} duration={800}>
            <div className="eyebrow eyebrow-light mb-5">Questions</div>
          </Reveal>
          <Reveal y={32} duration={900} delay={120} blur={3}>
            <h2 className="font-display font-black text-[#030712] leading-[1.02] text-[clamp(2.25rem,5vw,4rem)] mb-12">
              Everything you'd ask first.
            </h2>
          </Reveal>

          <Accordion
            type="single"
            collapsible
            className="w-full"
            data-testid={TEST_IDS.faqAccordion}
          >
            {FAQS.map((f, i) => (
              <Reveal key={f.q} y={20} duration={650} delay={i * 80}>
                <AccordionItem
                  value={`item-${i}`}
                  className="border-b border-[#030712]/10"
                >
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="text-left font-display font-bold text-lg md:text-xl text-[#030712] hover:no-underline py-6"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#4B5563] text-base leading-relaxed pb-6 pr-8">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              </Reveal>
            ))}
          </Accordion>

          <Reveal y={28} duration={800} delay={120}>
            <div className="mt-12 p-6 rounded-xl bg-[#030712] text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <div className="font-display font-bold text-lg leading-tight">
                  Still have a question?
                </div>
                <div className="text-white/70 text-sm">
                  Tap the chat button — talk to the actual AI we install.
                </div>
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">
                Bottom-Right →
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============================= */}
      {/* SECTION 8 — FINAL CTA (DARK) */}
      {/* ============================= */}
      <section
        id="register"
        className="section-dark grain py-28 md:py-36 border-t border-white/5"
      >
        <Orbs variant="center" />
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <Reveal y={24} duration={700}>
              <div className="eyebrow mb-6">Reserve your seat</div>
            </Reveal>
            <Reveal y={48} duration={1000} delay={120} blur={5}>
              <h2 className="font-display font-black text-white leading-[0.98] text-[clamp(2.5rem,6vw,5.5rem)] mb-6">
                Seats are limited.
                <br />
                <span className="text-[#D4AF37]">Don't be the business</span> that
                found out too late.
              </h2>
            </Reveal>
            <Reveal y={20} duration={800} delay={320}>
              <p className="text-white/75 text-lg md:text-xl max-w-2xl mx-auto">
                Reserve your free seat now. Takes 30 seconds.
              </p>
            </Reveal>
          </div>

          <Reveal y={32} duration={900} delay={400} scale={0.98}>
            <div className="max-w-3xl mx-auto">
              <RegistrationForm />
            </div>
          </Reveal>

          <Reveal y={16} duration={700} delay={620}>
            <div className="mt-10 flex items-center justify-center gap-2 text-white/55 text-sm">
              <ShieldCheck size={16} className="text-[#00F0FF]" />
              Your seat is held the moment you submit.
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============================= */}
      {/* FOOTER (WHITE) */}
      {/* ============================= */}
      <footer className="section-light py-14 border-t border-[#030712]/8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div className="flex items-center gap-3">
              <div
                className="h-10 w-10 rounded-md flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, #030712 0%, #1f2937 100%)",
                }}
              >
                <Sparkles size={18} className="text-[#D4AF37]" />
              </div>
              <div>
                <div className="font-display font-black text-[#030712] text-lg leading-none">
                  FAVOR <span className="text-[#0055ff]">STUDIO</span>
                </div>
                <div className="text-xs text-[#4B5563] mt-1">
                  AI Infrastructure · New York
                </div>
              </div>
            </div>

            <a
              href="mailto:contact@favorstudio.agency"
              className="inline-flex items-center gap-2 text-[#030712] hover:text-[#0055ff] transition font-medium"
              data-testid="footer-contact-email"
            >
              <Mail size={16} />
              contact@favorstudio.agency
            </a>

            <div className="flex items-center gap-3">
              <SocialIcon
                href="https://www.instagram.com/favor_dabaddie?igsh=djN4c2FocjNlcW0y&utm_source=qr"
                label="Instagram"
              >
                <Instagram size={16} />
              </SocialIcon>
              <SocialIcon
                href="https://www.linkedin.com/in/favor-studio-37821a37a?utm_source=share_via&utm_content=profile&utm_medium=member_ios"
                label="LinkedIn"
              >
                <Linkedin size={16} />
              </SocialIcon>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-[#030712]/8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-[#4B5563]">
            <div>© {new Date().getFullYear()} Favor Studio. All rights reserved.</div>
            <div className="uppercase tracking-[0.2em]">Always On Sales · Aug 1, 2026</div>
          </div>
        </div>
      </footer>
    </main>
  );
}

function MetaItem({ icon, label, value }) {
  return (
    <div
      className="glass rounded-lg px-4 py-3 flex items-center gap-3"
    >
      <span className="text-[#00F0FF]">{icon}</span>
      <div className="flex flex-col">
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/50">
          {label}
        </span>
        <span className="text-sm text-white font-medium leading-tight">
          {value}
        </span>
      </div>
    </div>
  );
}

function Pillar({ label, sub }) {
  return (
    <div
      className="glass rounded-xl p-6 text-center"
    >
      <div
        className="font-display font-black text-4xl mb-1"
        style={{
          background:
            "linear-gradient(135deg, #ffffff 0%, #00F0FF 100%)",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        {label}
      </div>
      <div className="text-xs uppercase tracking-[0.2em] text-white/55">
        {sub}
      </div>
    </div>
  );
}

function SocialIcon({ href, label, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-testid={`footer-social-${label.toLowerCase()}`}
      className="h-9 w-9 rounded-full border border-[#030712]/15 text-[#030712] hover:bg-[#030712] hover:text-white transition flex items-center justify-center"
    >
      {children}
    </a>
  );
}

